import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { FileUploadType } from '@/api/generated/api.enum';
import { uploadDirectFile } from './directFileUpload';

/** Every browser upload uses a fresh presigned POST, then returns a completed file for linking. */
export async function uploadFile(
  type: Components.Schemas.FileUploadType,
  file: File,
  thumbnail?: File | null,
): Promise<Components.Schemas.UploadedFileDto> {
  // Both uploads run in parallel; only the original's completion waits for the thumbnail. Without its thumbnail
  // the file simply shows itself.
  const thumbnailFileId = thumbnail
    ? uploadFile(FileUploadType.thumbnail, thumbnail).then(
        (uploaded) => uploaded.id,
        () => undefined,
      )
    : Promise.resolve(undefined);
  return uploadDirectFile(file, {
    initiate: async (selected, checksumSha256) => {
      const response = await Api.Routes.FileUpload.initiate(null, {
        type,
        filename: selected.name,
        byteSize: selected.size,
        checksumSha256,
      });
      return response.data;
    },
    complete: async (id) =>
      (await Api.Routes.FileUpload.complete({ id }, { thumbnailFileId: await thumbnailFileId })).data,
  });
}

export async function uploadAndLinkStudentAvatar(
  studentId: number,
  file: File,
): Promise<Components.Schemas.StudentDto> {
  const uploaded = await uploadFile(FileUploadType.studentAvatar, file);
  const response = await Api.Routes.AvatarPicture.setStudentAvatarPicture({ id: studentId }, { fileId: uploaded.id });
  return response.data;
}

/** Fresh, short-lived bucket URL for a file the current user may see. */
export async function getFileUrl(fileId: number): Promise<Components.Schemas.FileUrlDto> {
  const res = await Api.Routes.File.getSignedUrl({ id: fileId });
  return res.data;
}
